import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { partnerFetch } from "@/lib/partner-client";

interface OutcomeSummary {
  id: string;
  slug: string;
  name: string;
  status: string;
  price_tokens: number;
  rating_avg: number;
  rating_count: number;
  partner_slug: string;
}

export function DashboardPage() {
  const { data } = useQuery({
    queryKey: ["dev", "catalog"],
    queryFn: async () => {
      // Filter to the partner's own outcomes by reading partner_slug from /v1/partner/me later.
      // For now, the catalog endpoint is public; partners see all published.
      const res = await partnerFetch("/v1/catalog?limit=50");
      if (!res.ok) throw new Error(`catalog fetch: ${res.status}`);
      return (await res.json()) as { items: OutcomeSummary[] };
    },
  });

  const outcomes = data?.items ?? [];

  return (
    <div className="mx-auto max-w-6xl px-4 py-12">
      <h1 className="text-3xl font-semibold tracking-tight">Dashboard</h1>
      <p className="mt-1 text-sm text-neutral-500">
        Your outcomes, credentials, and request volume at a glance.
      </p>

      <section className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Stat label="Outcomes" value={String(outcomes.length)} />
        <Stat
          label="Published"
          value={String(outcomes.filter((o) => o.status === "published").length)}
        />
        <Stat
          label="Pending review"
          value={String(outcomes.filter((o) => o.status === "pending").length)}
        />
      </section>

      <section className="mt-10">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-medium text-neutral-500">Your outcomes</h2>
          <Link
            to="/outcomes"
            className="text-xs text-neutral-500 hover:underline"
          >
            Manage →
          </Link>
        </div>
        {outcomes.length === 0 ? (
          <p className="mt-4 rounded-lg border border-dashed border-neutral-300 p-6 text-center text-sm text-neutral-500 dark:border-neutral-700">
            No outcomes yet. Register your first via{" "}
            <code className="font-mono text-xs">PUT /v1/catalog/outcomes/:slug</code>{" "}
            or run <code className="font-mono text-xs">appsumo schema push</code>{" "}
            from the SDK.
          </p>
        ) : (
          <ul className="mt-4 divide-y divide-neutral-200 overflow-hidden rounded-xl border border-neutral-200 dark:divide-neutral-800 dark:border-neutral-800">
            {outcomes.slice(0, 5).map((o) => (
              <li
                key={o.id}
                className="flex items-center justify-between bg-white px-5 py-3 text-sm dark:bg-neutral-950"
              >
                <div>
                  <p className="font-medium">{o.name}</p>
                  <p className="text-xs text-neutral-500">
                    {o.partner_slug}/{o.slug}
                  </p>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-xs text-neutral-500">
                    {o.price_tokens} tokens
                  </span>
                  <span
                    className={
                      o.status === "published"
                        ? "inline-flex rounded-full bg-emerald-100 px-2 py-0.5 text-xs text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300"
                        : "inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-700 dark:bg-amber-950 dark:text-amber-300"
                    }
                  >
                    {o.status}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="mt-10">
        <h2 className="text-sm font-medium text-neutral-500">Quick actions</h2>
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <ActionCard
            title="Credentials"
            description="View your client_id and manage webhook signing secrets."
            to="/credentials"
          />
          <ActionCard
            title="Docs"
            description="Quickstarts, API reference, and llms.txt."
            href="https://docs.sumo.com"
          />
          <ActionCard
            title="Status"
            description="Hub uptime + signed dispatch health."
            href="https://status.sumo.com"
          />
        </div>
      </section>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-6 dark:border-neutral-800 dark:bg-neutral-950">
      <p className="text-xs uppercase tracking-wide text-neutral-500">{label}</p>
      <p className="mt-2 text-3xl font-semibold">{value}</p>
    </div>
  );
}

function ActionCard({
  title,
  description,
  to,
  href,
}: {
  title: string;
  description: string;
  to?: string;
  href?: string;
}) {
  const cls =
    "block rounded-xl border border-neutral-200 bg-white p-5 transition hover:border-emerald-300 dark:border-neutral-800 dark:bg-neutral-950 dark:hover:border-emerald-700";
  const content = (
    <>
      <p className="font-medium">{title} →</p>
      <p className="mt-1 text-xs text-neutral-500">{description}</p>
    </>
  );
  if (to) return <Link to={to} className={cls}>{content}</Link>;
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className={cls}>
      {content}
    </a>
  );
}
